/**
 * RealTimePricing Component
 *
 * Displays real-time pricing estimates using the backend pricing API.
 * Shows loading state, error handling, and pricing breakdown.
 */

import { usePricingEstimation } from '../../hooks/usePricingEstimation';
import { ErrorAlert } from '../widget/ErrorAlert';
import { Skeleton } from '../common/Skeleton';
import { EnhancedPricingBreakdown, type PricingLineItem, type ConditionalWarning } from './EnhancedPricingBreakdown';
import { useRetry } from '../../hooks/useRetry';
import type { EstimatePriceRequest } from '../../types/pricing-estimation';

export interface RealTimePricingProps {
  providerSlug: string;
  params: EstimatePriceRequest | null;
}

export function RealTimePricing({
  providerSlug,
  params,
}: RealTimePricingProps) {
  const { retry, isRetrying } = useRetry();

  // Build the pricing request params
  const pricingParams: EstimatePriceRequest | null = params;

  const { pricing, isCalculating, error, refetch } = usePricingEstimation(
    providerSlug,
    pricingParams
  );

  // Don't render if no params provided
  if (!pricingParams) {
    return null;
  }

  // Loading state
  if (isCalculating) {
    return (
      <div className="space-y-3" data-testid="pricing-loading">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-8 w-40" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <ErrorAlert
        error={{
          message: 'Unable to calculate pricing',
          suggestion: 'Please try again or continue to checkout for final pricing',
        }}
        onRetry={() => retry(refetch)}
        isRetrying={isRetrying}
        dismissible
      />
    );
  }

  // Success state
  if (pricing) {
    // Transform pricing data to line items for EnhancedPricingBreakdown
    const lineItems: PricingLineItem[] = [
      {
        id: 'base',
        label: 'Base Price',
        amount: pricing.basePrice,
        tooltip: pricing.includedTonnage
          ? `Includes up to ${pricing.includedTonnage} tons`
          : undefined,
        type: 'base',
      },
    ];

    // Add overage fee if present
    if (pricing.overageFee && pricing.overageFee > 0) {
      lineItems.push({
        id: 'overage',
        label: 'Tonnage Overage',
        amount: pricing.overageFee,
        tooltip: 'Additional tonnage fees',
        type: 'fee',
      });
    }

    // Add overweight fee if present
    if (pricing.overweightFee && pricing.overweightFee > 0) {
      lineItems.push({
        id: 'overweight',
        label: 'Overweight Fee',
        amount: pricing.overweightFee,
        tooltip: 'Loads over 10 tons',
        type: 'fee',
      });
    }

    // Add service fees
    if (pricing.serviceFees && pricing.serviceFees.length > 0) {
      pricing.serviceFees.forEach(fee => {
        lineItems.push({
          id: fee.serviceId,
          label: fee.serviceName,
          amount: fee.amount,
          tooltip: `Quantity: ${fee.quantity}`,
          type: 'fee',
        });
      });
    }

    // Transform warnings to conditional warnings for the breakdown component
    const conditionalWarnings: ConditionalWarning[] = pricing.warnings
      .filter(w => w.estimatedCostImpact !== undefined && w.estimatedCostImpact > 0)
      .map(w => ({
        id: w.type,
        condition: w.message,
        description: w.severity === 'WARNING'
          ? 'This may result in additional charges'
          : 'This may affect your final pricing',
        estimatedCost: w.estimatedCostImpact || 0,
        severity: w.severity.toLowerCase() as 'info' | 'warning' | 'error',
      }));

    return (
      <div data-testid="pricing-breakdown">
        <EnhancedPricingBreakdown
          lineItems={lineItems}
          conditionalWarnings={conditionalWarnings}
          subtotal={pricing.totalEstimate}
          total={pricing.totalEstimate}
          disclaimer="Final pricing confirmed at checkout"
          collapsible={false}
        />
        {pricing.notes && pricing.notes.length > 0 && (
          <div className="mt-2 text-xs text-gray-600 space-y-1">
            {pricing.notes.map((note, idx) => (
              <p key={idx}>{note}</p>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}
